import React from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Globe, Zap, Trophy, TrendingUp, Users, Rocket } from "lucide-react";

const PivotEcosystem = () => {
  const upcomingApps = [
    { 
      name: "Pivot Point Pro", 
      description: "Skills training - shooting, ball handling, footwork",
      icon: "🏀",
      color: "blue"
    },
    { 
      name: "Pivot Analytics", 
      description: "Data tracking with sensor integration",
      icon: "📊",
      color: "purple"
    },
    { 
      name: "Pivot Recovery", 
      description: "Sleep, nutrition, and injury prevention",
      icon: "💤",
      color: "green"
    },
    { 
      name: "Pivot Performance", 
      description: "Summer conditioning (You're here!)",
      icon: "🔥",
      color: "orange"
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-6 lg:p-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="w-24 h-24 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border-2 border-indigo-500/30 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Globe className="w-12 h-12 text-indigo-500" />
          </motion.div>
          
          <h1 className="text-4xl md:text-5xl font-heading font-black text-white tracking-tight uppercase mb-4">
            PIVOT{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-500">
              ECOSYSTEM
            </span>
          </h1>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-lg px-4 py-2 font-black">
              <Rocket className="w-5 h-5 mr-2" />
              COMING SOON
            </Badge>
          </motion.div>
        </div>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center mb-12"
        >
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto leading-relaxed">
            The complete basketball development platform. Connect all your Pivot apps 
            to sync progress, share achievements, and compete with other athletes.
          </p>
        </motion.div>

        {/* Upcoming Apps Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="grid md:grid-cols-2 gap-4 mb-12"
        >
          {upcomingApps.map((app, idx) => (
            <motion.div
              key={app.name}
              initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + idx * 0.1 }}
            >
              <Card className={`bg-zinc-900/80 border border-${app.color}-500/30 hover:border-${app.color}-500/50 transition-all ${app.name.includes('Performance') ? 'ring-2 ring-orange-500/50' : 'opacity-60'}`}>
                <CardContent className="p-6 flex items-center gap-4">
                  <div className={`w-14 h-14 bg-${app.color}-500/10 border border-${app.color}-500/30 rounded-lg flex items-center justify-center text-2xl`}>
                    {app.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading font-bold text-white">{app.name}</h3>
                      {app.name.includes('Performance') && (
                        <Badge className="bg-green-500/20 text-green-400 text-[10px]">ACTIVE</Badge>
                      )}
                    </div>
                    <p className="text-sm text-zinc-500">{app.description}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Features Coming */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <Card className="bg-gradient-to-br from-indigo-600/10 to-purple-600/10 border border-indigo-500/30">
            <CardContent className="p-8">
              <h2 className="text-xl font-heading font-bold text-white uppercase tracking-tight mb-6 text-center">
                What's Coming
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { icon: Zap, title: "Cross-App Sync", desc: "Unified progress across all Pivot apps" },
                  { icon: Trophy, title: "Global Leaderboards", desc: "Compete with athletes worldwide" },
                  { icon: Users, title: "Team Features", desc: "Train together, grow together" }
                ].map((feature, idx) => (
                  <div key={idx} className="text-center">
                    <div className="w-12 h-12 bg-indigo-500/20 border border-indigo-500/30 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <feature.icon className="w-6 h-6 text-indigo-400" />
                    </div>
                    <h3 className="font-bold text-white text-sm mb-1">{feature.title}</h3>
                    <p className="text-xs text-zinc-500">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
          className="text-center mt-8"
        >
          <p className="text-zinc-600 text-sm">
            Stay tuned for updates • Join the beta to be first in line
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PivotEcosystem;
